<template>
  <section>
    <nav class="level">
      <div class="level-left">
        <div class="level-item">
          <b-field>
            <b-autocomplete
              ref="collaborate_selector"
              v-model="collaborate_name"
              name="collaborate"
              field="name"
              :data="filteredCollaborates"
              :loading="loadingCollaborates"
              :placeholder="
                $t('attributes.collaborate') + $t('attributes.name')
              "
              :open-on-focus="true"
              @typing="filteringCollaborates"
              @select="onSelectCollaborate"
            >
              <template #empty>
                No results found
              </template>
            </b-autocomplete>
          </b-field>
        </div>
        <div class="level-item">
          <b-field class="activity-selector">
            <b-autocomplete
              ref="activity_selector"
              v-model="activities_name"
              name="collaborate"
              field="name"
              :data="filteredActivities"
              :loading="loadingActivities"
              :placeholder="
                $t('attributes.collaborate_activity') + $t('attributes.name')
              "
              :open-on-focus="true"
              @typing="filteringActivities"
              @select="onSelectActivity"
            >
              <template #empty>
                {{
                  !selectedCollaborateId ? '請先選擇企業' : '此企業尚未建立活動'
                }}
              </template>
            </b-autocomplete>
          </b-field>
        </div>
        <div class="level-item">
          <div class="buttons">
            <b-button
              type="is-primary"
              :disabled="!canExport"
              @click="onExportActivity"
              outlined
            >
              {{ $t('message.export_activity') }}
            </b-button>
            <b-button
              type="is-info"
              :disabled="!canExport"
              @click="onExportAll"
            >
              {{ $t('message.export_all_reports') }}
            </b-button>
          </div>
        </div>
      </div>
      <div class="level-right">
        <div class="level-item">
          <div class="buttons">
            <b-button
              type="is-success"
              :disabled="!canExport"
              @click="openSelectFieldsModel"
            >
              {{ $t('message.select_fields') }}
            </b-button>
            <b-button
              type="is-warning"
              :disabled="!canExport"
              @click="openRenameFieldsModel"
            >
              {{ $t('message.rename_fields') }}
            </b-button>
            <b-button
              v-if="!exportConfig || exportConfig.length === 0"
              type="is-primary"
              :disabled="!canExport"
              @click="onExport"
            >
              {{ $t('message.export_traveler_reports') }}
            </b-button>
            <b-dropdown
              v-else
              position="is-bottom-left"
              hoverable
              aria-role="list"
            >
              <template #trigger>
                <button class="button is-primary">
                  <span>{{ $t('message.export_traveler_reports') }}</span>
                  <b-icon icon="menu-down" />
                </button>
              </template>

              <b-dropdown-item
                v-for="(config, index) in exportConfig"
                :key="index"
                :disabled="!canExport"
                @click="() => onExport(config.name)"
                aria-role="listitem"
              >
                {{ config.name }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </div>
      </div>
    </nav>

    <nav class="level">
      <!-- Left side -->
      <div class="level-left">
        <div class="level-item">
          <p class="title is-4">企業團</p>
        </div>
      </div>

      <div class="level-right">
        <div class="level-item">
          <div class="buttons">
            <button
              class="button is-primary"
              @click.prevent="reloadTravelGroups"
            >
              重新載入
            </button>
          </div>
        </div>
      </div>
    </nav>

    <b-table
      :data="groups"
      :loading="isLoading"
      :hoverable="true"
      :narrowed="true"
      :mobile-cards="true"
      :checked-rows="selectedGroups"
      checkable
      @check="onSelectGroups"
    >
      <b-table-column
        field="code"
        :label="$t('attributes.collaborate_group_code')"
        width="160"
        v-slot="props"
      >
        {{ props.row.code }}
      </b-table-column>

      <b-table-column
        field="shelve_on"
        :label="$t('attributes.activation_status')"
        width="90"
        v-slot="props"
      >
        <b-tag
          :type="props.row.shelve_on ? 'is-info' : 'is-danger'"
          size="is-medium"
        >
          {{ shelveStatus(props.row.shelve_on) }}
        </b-tag>
      </b-table-column>

      <b-table-column
        field="days"
        :label="$t('attributes.days')"
        width="85"
        v-slot="props"
      >
        {{ props.row.days }}
      </b-table-column>

      <b-table-column
        field="departure_date"
        :label="$t('attributes.departure_date')"
        width="150"
        v-slot="props"
      >
        {{ formatDate(props.row.departure_date) }}
      </b-table-column>

      <b-table-column
        field="sign_up_deadline"
        :label="$t('attributes.signup_deadline')"
        width="150"
        v-slot="props"
      >
        {{ formatDate(props.row.sign_up_deadline) }}
      </b-table-column>

      <b-table-column
        field="registration_start_date"
        :label="$t('attributes.registration_range')"
        width="150"
        v-slot="props"
      >
        {{ formatDate(props.row.registration_start_date) }} -
        {{ formatDate(props.row.registration_end_date) }}
      </b-table-column>

      <b-table-column
        field="name"
        :label="$t('attributes.collaborate_group') + $t('attributes.name')"
        width="300"
        v-slot="props"
      >
        <!-- eslint-disable-next-line-->
        <p v-html="props.row.name"></p>
      </b-table-column>

      <b-table-column
        field="user_complete_filled_quantity"
        label="員工報名＃"
        :subheading="userQuantity.join('/')"
        width="200"
        numeric
      >
        <template v-slot:header="{ column }">
          <b-tooltip label="資料完整/報名完成/預報" append-to-body dashed>
            {{ column.label }}
          </b-tooltip>
        </template>
        <template v-slot:subheading>
          {{ userQuantity.join('/') }}
        </template>
        <template v-slot:default="groupProps">
          {{ groupProps.row.user_complete_filled_quantity }} /
          {{ groupProps.row.user_completed_quantity }} /
          {{ groupProps.row.user_quantity }}
        </template>
      </b-table-column>

      <b-table-column
        field="sign_up_complete_filled_quantity"
        label="旅客＃"
        :subheading="personQuantity.join('/')"
        width="200"
        numeric
      >
        <template v-slot:header="{ column }">
          <b-tooltip label="資料完整/報名完成/預報" append-to-body dashed>
            {{ column.label }}
          </b-tooltip>
        </template>
        <template v-slot:subheading>
          {{ personQuantity.join('/') }}
        </template>
        <template v-slot:default="groupProps">
          {{ groupProps.row.sign_up_complete_filled_quantity }} /
          {{ groupProps.row.sign_up_completed_quantity }} /
          {{ groupProps.row.sign_up_quantity }}
        </template>
      </b-table-column>

      <b-table-column
        field="quantity"
        label="名額"
        width="70"
        numeric
        v-slot="props"
      >
        {{ props.row.quantity }}
      </b-table-column>

      <b-table-column
        field="is_full"
        :label="$t('attributes.fulled')"
        width="95"
        v-slot="props"
      >
        <b-tag
          :type="props.row.is_full ? 'is-danger' : 'is-info'"
          size="is-medium"
        >
          {{
            $t(
              props.row.is_full ? 'attributes.fulled' : 'attributes.not_fulled'
            )
          }}
        </b-tag>
      </b-table-column>

      <b-table-column
        field="is_expired"
        :label="$t('attributes.expired')"
        width="110"
        v-slot="props"
      >
        <b-tag
          :type="props.row.is_expired ? 'is-danger' : 'is-info'"
          size="is-medium"
        >
          {{
            $t(
              props.row.is_expired
                ? 'attributes.has_expired'
                : 'attributes.not_expired'
            )
          }}
        </b-tag>
      </b-table-column>

      <template #empty>
        <section class="section">
          <div class="content has-text-grey has-text-centered">
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
            <p v-if="selectedActivity">{{ $t('attributes.empty') }}</p>
            <p v-else-if="selectedCollaborateId">請選擇活動</p>
            <p v-else>請先選擇企業</p>
          </div>
        </section>
      </template>
    </b-table>

    <hr />

    <p class="title is-4">訂單</p>

    <nav class="level">
      <div class="level-left">
        <div class="level-item">
          <b-field>
            <b-input
              :value="searchName"
              :loading="loading"
              :disabled="loading"
              @input="searchUser"
              placeholder="搜尋旅客"
            />
          </b-field>
        </div>
        <div class="level-item">
          <b-field>
            <b-select
              :value="filterStatus"
              :loading="loading"
              :disabled="loading"
              @input="onFilterStatus"
              placeholder="選擇訂單狀態"
            >
              <option value="-99">全部訂單</option>
              <option
                v-for="option in orderStatusOptions"
                :key="option.key"
                :value="option.key"
              >
                {{ option.value }}
              </option>
            </b-select>
          </b-field>
        </div>
      </div>
    </nav>

    <b-table
      :data="orders"
      :selected.sync="selectedOrder"
      :loading="isLoading"
      :hoverable="true"
      :narrowed="true"
      :mobile-cards="true"
      paginated
      backend-pagination
      backend-sorting
      :total="orderTotal"
      :per-page="orderPerPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[orderSortField, orderSortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @select="onSelectOrder"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <b-table-column
        field="order_no"
        :label="$t('attributes.order_no')"
        sortable
        v-slot="props"
      >
        {{ props.row.order_no }}
      </b-table-column>

      <b-table-column
        field="user_name"
        :label="$t('attributes.user_name')"
        sortable
        v-slot="props"
      >
        {{ props.row.user_name }}
      </b-table-column>

      <b-table-column
        field="user_account"
        :label="$t('attributes.account')"
        sortable
        v-slot="props"
      >
        {{ props.row.user_account }}
      </b-table-column>

      <b-table-column
        field="group_code"
        :label="$t('attributes.collaborate_group_code')"
        sortable
        v-slot="props"
      >
        {{ props.row.group_code }}
      </b-table-column>

      <b-table-column
        field="group_name"
        :label="$t('attributes.collaborate_group')"
        sortable
        v-slot="props"
      >
        {{ props.row.group_name }}
      </b-table-column>

      <b-table-column
        field="status"
        :label="$t('attributes.signup_status') + '?'"
        sortable
        v-slot="props"
      >
        {{ order_status(props.row) }}
      </b-table-column>

      <b-table-column
        field="details_count"
        :label="
          `${$t('attributes.details_free_count')}/${$t(
            'attributes.details_count'
          )}`
        "
        centered
        sortable
        v-slot="props"
      >
        {{ props.row.details_free_count }} / {{ props.row.details_count }}
      </b-table-column>

      <b-table-column
        field="subsidy"
        :label="$t('attributes.every_one') + $t('attributes.subsidy')"
        numeric
        sortable
        v-slot="props"
      >
        {{ props.row.subsidy }}
      </b-table-column>

      <b-table-column
        field="total_fee"
        :label="$t('attributes.total_fee')"
        numeric
        sortable
        v-slot="props"
      >
        {{ props.row.total_fee }}
      </b-table-column>

      <b-table-column
        field="total_cost"
        :label="$t('attributes.total_cost')"
        numeric
        sortable
        v-slot="props"
      >
        {{ props.row.total_cost }}
      </b-table-column>

      <template #empty>
        <section class="section">
          <div class="content has-text-grey has-text-centered">
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
            <p v-if="selectedActivity">{{ $t('attributes.empty') }}</p>
            <p v-else-if="selectedCollaborateId">請選擇活動</p>
            <p v-else>請先選擇企業</p>
          </div>
        </section>
      </template>
    </b-table>

    <div class="card">
      <header class="card-header">
        <p class="card-header-title">
          帳號：{{ selectedOrder && selectedOrder.user_account }}
        </p>
        <p class="card-header-title">
          訂單：{{ selectedOrder && selectedOrder.order_no }}
        </p>
        <p class="card-header-title">
          補助：{{ selectedOrder && selectedOrder.subsidy }}
        </p>
        <p class="card-header-title">
          總金額：{{ selectedOrder && selectedOrder.total_fee }}
        </p>
        <p class="card-header-title">
          自費：{{ selectedOrder && selectedOrder.total_cost }}
        </p>
        <div class="card-header-title">
          <div class="buttons">
            <button
              v-show="selectedOrder"
              class="button is-primary"
              :disabled="!canModifyOrder"
              @click.prevent="openOrderUpdateModel"
            >
              更新訂單資訊
            </button>
            <button
              v-show="selectedOrder"
              class="button is-danger"
              :disabled="!canTransferOrder"
              @click.prevent="openTransferModel(selectedOrder)"
            >
              轉團
            </button>
          </div>
        </div>
      </header>
      <header class="card-header">
        <p class="card-header-title">
          會員：{{ selectedOrder && selectedOrder.user_name }}
        </p>
        <p
          v-if="
            selectedOrder &&
              selectedOrder.user_activity &&
              selectedActivity.region_enabled
          "
          class="card-header-title"
        >
          {{ selectedActivity.region_alias }}：{{
            selectedOrder.user_activity.region
          }}
        </p>
        <p
          v-if="
            selectedOrder &&
              selectedOrder.user_activity &&
              selectedActivity.area_enabled
          "
          class="card-header-title"
        >
          {{ selectedActivity.area_alias }}：{{
            selectedOrder.user_activity.area
          }}
        </p>
        <p
          v-if="
            selectedOrder &&
              selectedOrder.user_activity &&
              selectedActivity.department_enabled
          "
          class="card-header-title"
        >
          {{ selectedActivity.department_alias }}：{{
            selectedOrder.user_activity.department
          }}
        </p>
        <p
          v-if="
            selectedOrder &&
              selectedOrder.user_activity &&
              selectedActivity.section_enabled
          "
          class="card-header-title"
        >
          {{ selectedActivity.section_alias }}：{{
            selectedOrder.user_activity.section
          }}
        </p>
        <p
          v-if="selectedOrder && selectedOrder.user_activity"
          class="card-header-title"
        >
          組別：{{ selectedOrder.user_activity.member_status }}
        </p>
        <p
          v-if="selectedOrder && selectedOrder.user_activity"
          class="card-header-title"
        >
          梯次：{{ selectedOrder.user_activity.group_type }}
        </p>
      </header>
      <div class="card-content">
        <div class="content">
          <b-table
            :data="details"
            :hoverable="true"
            :narrowed="true"
            :mobile-cards="true"
            :loading="loadingOrderDetails"
            detailed
            detail-key="id"
            class="vertical-table"
            :show-detail-icon="true"
            :default-sort-direction="defaultSortOrder"
            :default-sort="[orderSortField, orderSortOrder]"
            aria-next-label="Next page"
            aria-previous-label="Previous page"
            aria-page-label="Page"
            aria-current-label="Current page"
          >
            <b-table-column
              field="is_free"
              :label="$t('attributes.is_free')"
              sortable
              v-slot="props"
            >
              {{
                props.row.is_free ? $t('attributes.yes') : $t('attributes.no')
              }}
            </b-table-column>

            <b-table-column
              field="name"
              :label="$t('attributes.name')"
              sortable
              v-slot="props"
            >
              {{ props.row.name }}
            </b-table-column>

            <b-table-column
              field="english_name"
              :label="$t('attributes.english_name')"
              sortable
              v-slot="props"
            >
              {{ props.row.english_name }}
            </b-table-column>

            <b-table-column
              field="emergency_contact"
              :label="$t('attributes.emergency_contact')"
              sortable
              v-slot="props"
            >
              {{ props.row.emergency_contact }}
            </b-table-column>

            <b-table-column
              field="emergency_phone"
              :label="$t('attributes.emergency_phone')"
              sortable
              v-slot="props"
            >
              {{ props.row.emergency_phone }}
            </b-table-column>

            <b-table-column
              field="gender"
              :label="$t('attributes.gender')"
              sortable
              v-slot="props"
            >
              {{ props.row.gender }}
            </b-table-column>

            <b-table-column
              field="date_of_birth"
              :label="$t('attributes.date_of_birth')"
              sortable
              v-slot="props"
            >
              {{
                props.row.date_of_birth
                  ? $dayjs(props.row.date_of_birth).format('YYYY-MM-DD')
                  : '未填寫'
              }}
            </b-table-column>

            <b-table-column
              field="age"
              :label="$t('attributes.age')"
              numeric
              sortable
              v-slot="props"
            >
              {{ props.row.age }}
            </b-table-column>

            <b-table-column
              field="age_type"
              :label="$t('attributes.age_type')"
              sortable
              v-slot="props"
            >
              {{ props.row.age_type_name }}
            </b-table-column>

            <b-table-column
              field="bed_type"
              :label="$t('attributes.bed_type')"
              sortable
              v-slot="props"
            >
              {{ props.row.bed_type_name }}
            </b-table-column>

            <b-table-column
              field="traveler_type"
              :label="$t('attributes.traveler_type')"
              sortable
              v-slot="props"
            >
              {{ props.row.traveler_type }}
            </b-table-column>

            <b-table-column
              field="identity_number"
              :label="$t('attributes.identity_number')"
              sortable
              v-slot="props"
            >
              {{ props.row.identity_number }}
            </b-table-column>

            <b-table-column
              field="relationship"
              :label="$t('attributes.relationship')"
              sortable
              v-slot="props"
            >
              {{ props.row.relationship }}
            </b-table-column>

            <b-table-column
              field="passport_number"
              :label="$t('attributes.passport_number')"
              sortable
              v-slot="props"
            >
              {{ props.row.passport_number }}
            </b-table-column>

            <b-table-column
              field="passport_expire"
              :label="$t('attributes.passport_expire')"
              sortable
              v-slot="props"
            >
              {{ formatDate(props.row.passport_expire) }}
            </b-table-column>

            <b-table-column
              field="mobile_phone"
              :label="$t('attributes.mobile_phone')"
              sortable
              v-slot="props"
            >
              {{ props.row.mobile_phone }}
            </b-table-column>

            <b-table-column
              field="tel"
              :label="$t('attributes.company_tel')"
              sortable
              v-slot="props"
            >
              {{ props.row.tel }}
            </b-table-column>

            <b-table-column
              field="travel_fee"
              :label="$t('attributes.travel_fee')"
              numeric
              sortable
              v-slot="props"
            >
              {{ props.row.travel_fee }}
            </b-table-column>

            <b-table-column
              field="subsidy"
              :label="$t('attributes.subsidy')"
              numeric
              sortable
              v-slot="props"
            >
              {{ props.row.subsidy }}
            </b-table-column>

            <b-table-column
              field="travel_cost"
              :label="$t('attributes.travel_cost')"
              numeric
              sortable
              v-slot="props"
            >
              {{ props.row.travel_cost }}
            </b-table-column>

            <b-table-column
              field="commons_cost"
              :label="$t('attributes.commons_cost')"
              numeric
              sortable
              v-slot="props"
            >
              {{ props.row.commons_cost }}
            </b-table-column>

            <b-table-column
              field="spots_cost"
              :label="$t('attributes.spots_cost')"
              numeric
              sortable
              v-slot="props"
            >
              {{ props.row.spots_cost }}
            </b-table-column>

            <b-table-column
              field="total_cost"
              :label="$t('attributes.total_fee')"
              numeric
              sortable
              v-slot="props"
            >
              {{ props.row.total_cost }}
            </b-table-column>

            <template #detail="props">
              <ul style="margin-top: 0">
                <li>
                  {{ $t('attributes.home_address') }}：{{
                    props.row.home_address
                  }}
                </li>
                <li>
                  {{ $t('attributes.passport_image') }}：<a
                    :href="passportImage(props.row.passport_image)"
                    @click.prevent="onClickImage(props.row.passport_image)"
                    >{{ props.row.passport_image }}</a
                  >
                </li>
                <li>{{ $t('attributes.remark') }}：{{ props.row.remark }}</li>
                <li>
                  <span>參團資料</span>
                  <ul style="margin-top: 0">
                    <li
                      v-for="(answer, index) in props.row.common_answers"
                      :key="index"
                    >
                      <p v-if="answer.type === 4" class="is-flex">
                        <span>{{ answer.title }}:</span>
                        <img
                          :src="answer.answer"
                          :alt="answer.title"
                          style="height: 160px; padding-left: 8px;"
                        />
                      </p>
                      <p v-else>
                        {{ answer.title }}: {{ answer.answer || '未填寫' }}
                      </p>
                    </li>
                  </ul>
                </li>
                <li>
                  <span>參團行程</span>
                  <ul style="margin-top: 0">
                    <li
                      v-for="(answer, index) in props.row.spot_answers"
                      :key="index"
                    >
                      {{ answer }}
                    </li>
                  </ul>
                </li>
              </ul>
            </template>

            <template #empty>
              <section class="section">
                <div class="content has-text-grey has-text-centered">
                  <p>
                    <b-icon icon="emoticon-sad" size="is-large" />
                  </p>
                  <p v-if="selectedOrder">{{ $t('attributes.empty') }}</p>
                  <p v-else>請先選擇訂單</p>
                </div>
              </section>
            </template>
          </b-table>
        </div>
      </div>
    </div>

    <b-modal :active.sync="isImageModalActive">
      <p class="image">
        <img :src="passportImage(selectedImageUrl)" alt="" />
      </p>
    </b-modal>

    <b-modal
      :active.sync="isSelectFieldsModelActive"
      :can-cancel="['escape', 'x']"
      scroll="keep"
      has-modal-card
    >
      <select-fields-model
        :activity-id="selectedActivityId"
        :export-config="exportConfig"
        :fields="fields"
        @save-fields="onSaveActivityExportFields"
      />
    </b-modal>

    <b-modal
      :active.sync="isRenameFieldsModelActive"
      :can-cancel="['escape', 'x']"
      scroll="keep"
      has-modal-card
    >
      <rename-fields-model
        :activity-id="selectedActivityId"
        :export-config="exportConfig"
        :fields="fields"
        @save-fields="onSaveActivityExportFields"
      />
    </b-modal>

    <b-modal
      :active.sync="isTransferModelActive"
      :can-cancel="['escape', 'x']"
      scroll="keep"
      has-modal-card
    >
      <order-transfer-form
        :order="transferOrder"
        @onComplete="onTransferCompleted"
      />
    </b-modal>

    <b-modal
      :active.sync="isOrderUpdateModelActive"
      :width="1200"
      :can-cancel="['escape', 'x']"
      scroll="keep"
      has-modal-card
    >
      <order-update-form
        :orderNo="selectedOrder && selectedOrder.order_no"
        @onComplete="onOrderUpdateCompleted"
      />
    </b-modal>
  </section>
</template>

<script>
import debounce from 'lodash/debounce'
import {
  TRAVEL_GROUPS_API,
  COLLABORATE_ORDERS_API,
  FIRM_COLLABORATES_API,
  FIRM_COLLABORATES_ACTIVITIES_API,
  FIRM_EXPORT_FIELDS_API
} from '@/utils/apis'
import notifyService from '@/services/notifyService'
import SelectFieldsModel from '@/components/firm/SelectFieldsModel'
import RenameFieldsModel from '@/components/firm/RenameFieldsModel'
import OrderTransferForm from '@/components/firm/OrderTransferForm'
import OrderUpdateForm from '@/components/firm/OrderUpdateForm'

export default {
  name: 'CollaborateTravelGroups',
  components: {
    OrderUpdateForm,
    SelectFieldsModel,
    RenameFieldsModel,
    OrderTransferForm
  },
  data() {
    const orderStatusOptions = [
      {
        key: -1,
        value: '已取消報名'
      },
      {
        key: 0,
        value: '未報名'
      },
      {
        key: 1,
        value: '填寫中'
      },
      {
        key: 2,
        value: '報名完成'
      },
      {
        key: 3,
        value: '報名完成-資料不全'
      },
      {
        key: 4,
        value: '報名完成-變更尚未送出'
      },
      {
        key: 5,
        value: '(指定團)未查看'
      },
      {
        key: 6,
        value: '(指定團)查看未報名'
      },
      {
        key: 7,
        value: '(指定團)修改未報名'
      }
    ]

    return {
      loading: false,

      groups: [],
      selectedGroups: [],
      sortField: 'code',
      sortOrder: 'asc',
      userQuantity: [],
      personQuantity: [],

      orderStatusOptions,
      searchName: null,
      filterStatus: -99,
      orderTotal: 0,
      orders: [],
      selectedOrder: null,
      orderSortField: 'code',
      orderSortOrder: 'asc',
      defaultSortOrder: 'asc',
      orderPage: 1,
      orderPerPage: 10,

      loadingOrderDetails: false,
      details: [],

      loadingCollaborates: false,
      collaborate_name: '',
      collaborates: [],
      filteredCollaborates: [],
      selectedCollaborateId: null,

      loadingActivities: false,
      activities_name: '',
      activities: [],
      filteredActivities: [],
      selectedActivity: null,
      selectedActivityId: null,
      selectedActivityConfig: {},
      exportConfig: [],
      fields: [],

      isImageModalActive: false,
      selectedImageUrl: null,

      isSelectFieldsModelActive: false,
      isRenameFieldsModelActive: false,

      transferOrder: null,
      isTransferModelActive: false,

      isOrderUpdateModelActive: false
    }
  },
  computed: {
    isLoading() {
      return this.loading || this.loadingCollaborates || this.loadingActivities
    },
    canExport() {
      if (this.loading) {
        return false
      }

      return !!this.selectedActivity && !!this.selectedCollaborateId
    },
    canModifyOrder() {
      if (this.selectedOrder) {
        if (
          this.selectedOrder.status === this.ORDER_STATUS.FILLING ||
          this.selectedOrder.status === this.ORDER_STATUS.COMPLETED
        ) {
          return true
        }
      }

      return false
    },
    canTransferOrder() {
      return (
        this.selectedOrder &&
        this.selectedOrder.status === this.ORDER_STATUS.COMPLETED
      )
    }
  },
  mounted() {
    this.loadCollaborates()
  },
  methods: {
    async reloadTravelGroups() {
      if (!this.selectedActivity) {
        return
      }

      this.loading = true
      await this.loadExportFields()
      await this.loadTravelGroups()
      await this.loadUserOrders()
      this.loading = false
    },
    async reloadUserOrders() {
      if (!this.selectedActivity) {
        return
      }

      this.loading = true
      await this.loadUserOrders()
      this.loading = false
    },

    async loadCollaborates() {
      const api = `${FIRM_COLLABORATES_API}/list`

      this.loadingCollaborates = true

      this.collaborates = []
      this.filteredCollaborates = []
      this.selectedCollaborateId = null

      try {
        const res = await this.axios.get(api)
        this.collaborates = res.data.data
        this.filteringCollaborates()
      } catch (e) {
        notifyService.fetch_error('collaborate', e)
      } finally {
        this.loadingCollaborates = false
        this.$refs.collaborate_selector.focus()
      }
    },
    async loadActivities() {
      const api = `${FIRM_COLLABORATES_ACTIVITIES_API}/list`
      const params = {
        collaborate_id: this.selectedCollaborateId
      }

      this.loadingActivities = true

      this.activities = []
      this.filteredActivities = []
      this.selectedActivity = null
      this.selectedActivityId = null
      this.selectedActivityConfig = {}
      this.exportConfig = []
      this.details = []

      try {
        const res = await this.axios.get(api, { params })
        this.activities = res.data.data
        this.activities_name = ''
        this.filteringActivities()
        this.$refs.activity_selector.focus()
      } catch (e) {
        notifyService.fetch_error('collaborate_activity', e)
      } finally {
        this.loadingActivities = false
      }
    },
    async loadTravelGroups() {
      const api = `${TRAVEL_GROUPS_API}/report`
      const params = {
        activity_id: this.selectedActivity.id
      }

      this.groups = []
      this.selectedGroups = []
      this.userQuantity = []
      this.personQuantity = []

      try {
        const res = await this.axios.get(api, { params })

        this.groups = res.data.data
        this.selectedGroups = this.groups

        const userQuantity = this.groups.reduce(
          ({ filled, completed, preorder }, group) => {
            if (group.is_deprecated) {
              return {
                filled: filled,
                completed: completed,
                preorder: preorder
              }
            }

            return {
              filled: filled + group.user_complete_filled_quantity,
              completed: completed + group.user_completed_quantity,
              preorder: preorder + group.user_quantity
            }
          },
          { filled: 0, completed: 0, preorder: 0 }
        )

        this.userQuantity = [
          userQuantity.filled,
          userQuantity.completed,
          userQuantity.preorder
        ]
        const personQuantity = this.groups.reduce(
          ({ filled, completed, preorder }, group) => {
            if (group.is_deprecated) {
              return {
                filled: filled,
                completed: completed,
                preorder: preorder
              }
            }

            return {
              filled: filled + group.sign_up_complete_filled_quantity,
              completed: completed + group.sign_up_completed_quantity,
              preorder: preorder + group.sign_up_quantity
            }
          },
          { filled: 0, completed: 0, preorder: 0 }
        )
        this.personQuantity = [
          personQuantity.filled,
          personQuantity.completed,
          personQuantity.preorder
        ]
      } catch (e) {
        notifyService.fetch_error('collaborate_group', e)
      }
    },
    async loadExportFields() {
      const api = FIRM_EXPORT_FIELDS_API

      const params = {
        activity_id: this.selectedActivity.id
      }

      this.fields = []

      try {
        const res = await this.axios.get(api, { params })
        this.fields = res.data.data
      } catch (e) {
        notifyService.fetch_error('export_field', e)
      }
    },
    async loadUserOrders() {
      const api = COLLABORATE_ORDERS_API

      const params = {
        collaborate_id: this.selectedCollaborateId,
        activity_id: this.selectedActivity.id,
        search: this.searchName,
        status: this.filterStatus,
        groups: this.selectedGroups.map(g => g.code).join(','),
        sort_by: this.orderSortField,
        by_descending: this.orderSortOrder !== 'asc',
        page_size: this.orderPerPage,
        page_number: this.orderPage
      }

      this.orders = []
      this.orderTotal = 0
      this.selectedOrder = null
      this.details = []

      try {
        const res = await this.axios.get(api, { params })
        const data = res.data.data

        this.orders = data.items
        this.orderTotal = data.item_count
        this.selectedOrder = null
        this.details = []
      } catch (e) {
        notifyService.fetch_error('order', e)
      }
    },
    async onExportActivity() {
      const data = {
        collaborate_id: this.selectedCollaborateId,
        activity_id: this.selectedActivity.id
      }

      const api = `${COLLABORATE_ORDERS_API}/export-activity`

      const config = {
        headers: { Accept: 'application/vnd.ms-excel' },
        responseType: 'blob',
        errorHandle: {
          customMessage: `${this.$t('attributes.fetch_failed', [
            this.$t('attributes.order')
          ])}: `
        }
      }

      this.loading = true
      try {
        const response = await this.axios.post(api, data, config)

        const date = this.$dayjs().format('YYYYMMDDHHmmss')
        const fileName = `${this.collaborate_name}_${this.selectedActivity.name}_報名狀況_${date}`

        const blob = new Blob([response.data], { type: response.data.type })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        notifyService.error('下載失敗', e)
      } finally {
        this.loading = false
      }
    },
    async onSaveActivityExportFields(exportConfig) {
      const configuration = {
        ...this.selectedActivityConfig,
        exportConfig
      }

      const data = {
        configuration: JSON.stringify(configuration)
      }

      const api = `${FIRM_COLLABORATES_ACTIVITIES_API}/${this.selectedActivityId}/config`

      this.loading = true
      try {
        await this.axios.put(api, data)
        this.exportConfig = exportConfig
        this.selectedActivityConfig = configuration
        this.selectedActivity.configuration = JSON.stringify(configuration)

        this.isSelectFieldsModelActive = false
        this.isRenameFieldsModelActive = false
        notifyService.success('所選欄位已紀錄')
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.loading = false
      }
    },
    async onExport(configName) {
      let selectedConfig = null
      if (configName) {
        selectedConfig =
          this.exportConfig &&
          this.exportConfig.find(c => c.name === configName)
      }

      if (!selectedConfig) {
        return
      }

      const data = {
        collaborate_id: this.selectedCollaborateId,
        activity_id: this.selectedActivity.id,
        groups: this.selectedGroups.map(g => g.code).join(','),
        fields: selectedConfig && selectedConfig.fields
      }

      const api = `${COLLABORATE_ORDERS_API}/export`

      const config = {
        headers: { Accept: 'application/vnd.ms-excel' },
        responseType: 'blob',
        errorHandle: {
          customMessage: `${this.$t('attributes.fetch_failed', [
            this.$t('attributes.order')
          ])}: `
        }
      }

      this.loading = true
      try {
        const response = await this.axios.post(api, data, config)

        const date = this.$dayjs().format('YYYYMMDDHHmmss')
        const fileName = `${this.collaborate_name}_${this.selectedActivity.name}_${configName}_${date}`

        const blob = new Blob([response.data], { type: response.data.type })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        notifyService.error('下載失敗', e)
      } finally {
        this.loading = false
      }
    },
    async onExportAll() {
      const data = {
        collaborate_id: this.selectedCollaborateId,
        activity_id: this.selectedActivity.id,
        groups: this.selectedGroups.map(g => g.code).join(',')
      }

      const api = `${COLLABORATE_ORDERS_API}/export-all`

      const config = {
        headers: { Accept: 'application/vnd.ms-excel' },
        responseType: 'blob',
        errorHandle: {
          customMessage: `${this.$t('attributes.fetch_failed', [
            this.$t('attributes.order')
          ])}: `
        }
      }

      this.loading = true
      try {
        const response = await this.axios.post(api, data, config)

        const date = this.$dayjs().format('YYYYMMDDHHmmss')
        const fileName = `${this.collaborate_name}_${this.selectedActivity.name}_旅客比對資料_${date}`

        const blob = new Blob([response.data], { type: response.data.type })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        notifyService.error('下載失敗', e)
      } finally {
        this.loading = false
      }
    },

    filteringCollaborates: debounce(function() {
      this.filteredCollaborates = this.collaborates.filter(option => {
        return (
          option.name
            .toString()
            .toLowerCase()
            .indexOf(this.collaborate_name.toLowerCase()) >= 0
        )
      })
    }, 500),
    onSelectCollaborate(option) {
      const id = option && option.id

      if (this.selectedCollaborateId === id) {
        return
      }

      this.selectedCollaborateId = id

      this.activities_name = ''

      this.groups = []
      this.selectedGroups = []

      this.orderTotal = 0
      this.orders = []
      this.selectedOrder = null
      this.details = []

      this.loadActivities()
    },

    filteringActivities: debounce(function() {
      this.filteredActivities = this.activities.filter(option => {
        return (
          option.name
            .toString()
            .toLowerCase()
            .indexOf(this.activities_name.toLowerCase()) >= 0
        )
      })
    }, 500),
    onSelectActivity(option) {
      this.selectedActivity = option
      this.selectedActivityId = option && option.id
      this.selectedActivityConfig = JSON.parse(
        option ? option.configuration || '{}' : '{}'
      )
      this.exportConfig = this.selectedActivityConfig['exportConfig'] || []

      this.groups = []
      this.selectedGroups = []

      this.orderTotal = 0
      this.orders = []
      this.selectedOrder = null
      this.details = []

      this.reloadTravelGroups()
    },

    onClickImage(url) {
      this.isImageModalActive = true
      this.selectedImageUrl = url
    },
    onSelectGroups(groups) {
      this.selectedGroups = groups
      this.reloadUserOrders()
    },

    searchUser: debounce(function(name) {
      this.searchName = name
      this.reloadUserOrders()
    }, 500),
    onFilterStatus(option) {
      this.filterStatus = option
      this.reloadUserOrders()
    },
    async onSelectOrder(order) {
      try {
        const api = `${COLLABORATE_ORDERS_API}/${order.order_no}/details`

        this.loadingOrderDetails = true
        const res = await this.axios.get(api)
        const group = this.selectedGroups.find(g => g.code === order.group_code)
        const details = res.data.data

        this.details = details.map(detail => {
          const common_answers = group.common_questions.map(q => {
            const answer = detail.common_answers.find(a => a.title === q.title)
            return {
              type: q.type,
              title: q.title,
              answer: answer.answer
            }
          })
          const spot_answers = group.spot_questions.map(q => {
            const answer = detail.spot_answers.find(a => a.title === q.title)
            return (
              q.title +
              ': ' +
              (answer ? answer.selected_spot_name || '未填寫' : '未填寫')
            )
          })

          return { ...detail, common_answers, spot_answers }
        })
      } catch (e) {
        this.details = []
        notifyService.fetch_error('order_detail', e)
      } finally {
        this.loadingOrderDetails = false
      }
    },
    onPageChange(page) {
      this.orderPage = page
      this.reloadUserOrders()
    },
    onSort(field, order) {
      this.orderSortField = field
      this.orderSortOrder = order
      this.reloadUserOrders()
    },
    openSelectFieldsModel() {
      this.isSelectFieldsModelActive = true
    },
    openRenameFieldsModel() {
      this.isRenameFieldsModelActive = true
    },
    openTransferModel(order) {
      this.transferOrder = order
      this.isTransferModelActive = true
    },
    onTransferCompleted() {
      this.transferOrder = null
      this.isTransferModelActive = false
    },
    openOrderUpdateModel() {
      this.isOrderUpdateModelActive = true
    },
    onOrderUpdateCompleted() {
      this.isOrderUpdateModelActive = false
    },
    order_status(order) {
      switch (order.status) {
        case -1:
          return '已取消報名'
        case 0:
          return '未報名'
        case 1:
          return '填寫中' // 有進來查看企業團內容，未送出訂單
        case 2: {
          let status = '報名完成'
          status += order.is_complete_filled ? '' : '-資料不全'
          status += order.is_modified ? '-變更尚未送出' : ''
          return status
        }
        case 5:
          return '(指定團)未查看'
        case 6:
          return '(指定團)查看未報名'
        case 7:
          return '(指定團)修改未報名'
        default:
          return '狀態錯誤'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.b-table {
  margin-top: 0.75rem;
}

.vertical-table {
  overflow-y: auto;
}

.activity-selector {
  width: 200px;
}
</style>
